import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  getUserLanguages,
  deleteUserLanguage,
  getLanguages,
} from "../../../store/profile/actions";

import NoResults from "../../common/components/NoResults";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AlertDialog from "../components/AlertDialog";
import LanguageForm from "./LanguageForm";
import AdvertSkeleton from "../../common/components/AdvertSkeleton";
import LockIcon from "@mui/icons-material/Lock";
import moment from "moment";

export const LanguageList = (props) => {
  const {
    userProfile,
    env,
    loading,
    onWait,
    userLanguages,
    getUserLanguages,
    deleteUserLanguage,
    lookup,
    getLanguages,
    canEdit,
  } = props;

  const [formAction, setFormAction] = useState(null);
  const [deleteId, setDeleteId] = React.useState("");

  useEffect(() => {
    getUserLanguages(userProfile.id);

    if (!lookup?.languages?.length) getLanguages();
  }, []);

  const handleDelete = () => {
    deleteUserLanguage(deleteId);
    setDeleteId("");
  };

  const [showLanguageForm, setShowLanguageForm] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  return (
    <>
      <Card className={`border rounded mb-3`} elevation={1} id="languages">
        <CardContent>
          {loading && !onWait && !userLanguages.length ? (
            <AdvertSkeleton />
          ) : (
            <div className="container-fluid">
              <div className="d-flex flex-row justify-content-between align-items-center bg-light border px-3 py-1">
                {/* title */}
                <div>
                  <Typography variant="h5" style={{ color: "#078ece" }}>
                    Languages
                  </Typography>
                </div>
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    disabled={loading}
                    onClick={() => setShowLanguageForm(true)}
                    size="small"
                  >
                    <span className="d-none d-md-inline mr-1">Add</span> new
                  </Button>
                )}
              </div>

              {userLanguages.length === 0 ? (
                <NoResults />
              ) : (
                userLanguages.map((language, index) => (
                  <LanguageItemCard
                    key={language.id}
                    index={index}
                    language={language}
                    loading={loading}
                    deleteUserLanguage={deleteUserLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    setShowLanguageForm={setShowLanguageForm}
                    env={env}
                    canEdit={canEdit}
                  />
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showLanguageForm && (
        <LanguageForm
          onShow={showLanguageForm}
          onClose={() => {
            setShowLanguageForm(false);
            setSelectedLanguage(null);
          }}
          selectedLanguage={selectedLanguage}
        />
      )}
    </>
  );
};

const mapStateToProps = ({
  userProfile,
  env,
  loading,
  onWait,
  userLanguages,
  lookup,
}) => ({
  userProfile,
  env,
  loading,
  onWait,
  userLanguages,
  lookup,
});

export default connect(mapStateToProps, {
  getUserLanguages,
  deleteUserLanguage,
  getLanguages,
})(LanguageList);

const LanguageItemCard = (props) => {
  const {
    index,
    env,
    loading,
    language,
    deleteUserLanguage,
    setSelectedLanguage,
    setShowLanguageForm,
    canEdit,
  } = props;

  const [onDelete, setOnDelete] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Card
        className={`mb-2 rounded hoverable-card`}
        elevation={0}
        variant="outlined"
        style={{
          backgroundColor: "#fff",
          border: "none",
        }}
      >
        <CardHeader
          title={<Typography>{language.language.name}</Typography>}
          subheader={
            <>
              <div className="d-flex  mb-2">
                <div className="mr-3">
                  <span className="mr-1">Reading:</span>
                  <span
                    className={`badge badge-${
                      language.reading === "Excellent"
                        ? "success"
                        : language.reading === "Very Good"
                        ? "primary"
                        : "secondary"
                    }`}
                  >
                    {language.reading}
                  </span>
                </div>
                <div className="mr-3">
                  <span className="mr-1">Writing:</span>
                  <span
                    className={`badge badge-${
                      language.writting === "Excellent"
                        ? "success"
                        : language.writting === "Very Good"
                        ? "primary"
                        : "secondary"
                    }`}
                  >
                    {language.writting}
                  </span>
                </div>
                <div className="mr-3">
                  <span className="mr-1">Speaking:</span>
                  <span
                    className={`badge badge-${
                      language.speaking === "Excellent"
                        ? "success"
                        : language.speaking === "Very Good"
                        ? "primary"
                        : "secondary"
                    }`}
                  >
                    {language.speaking}
                  </span>
                </div>
              </div>
              {!!language.updatedOn && (
                <p
                  className="small text-muted mb-0 rounded-3 mt-2 mr-2 float-end"
                  style={{
                    fontSize: "11px",
                  }}
                >
                  Updated on, {moment(language.updatedOn).format("ll")}
                </p>
              )}
              {!language.updatedOn && (
                <p
                  className="small text-muted mb-0 rounded-3 mt-2 mr-2 float-end"
                  style={{
                    fontSize: "11px",
                  }}
                >
                  Created on, {moment(language.createdOn).format("ll")}
                </p>
              )}
            </>
          }
          action={
            <>
              {language.locked && (
                <IconButton
                  disableRipple
                  size="small"
                  className=""
                  aria-label="settings"
                >
                  <LockIcon />
                </IconButton>
              )}

              {(!language.locked || env !== "prod") && canEdit && (
                <>
                  <IconButton
                    size="small"
                    onClick={handleOpenMenu}
                    aria-label="settings"
                  >
                    <Badge color="info">
                      <span className="material-icons">more_vert</span>
                    </Badge>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={open}
                    onClose={handleCloseMenu}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        overflow: "visible",
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                        mt: 1.5,
                        "& .MuiAvatar-root": {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 2,
                        },
                        "&:before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 15,
                          width: 10,
                          height: 10,
                          bgcolor: "background.paper",
                          transform: "translateY(-50%) rotate(45deg)",
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <span>
                      <MenuItem
                        disabled={loading}
                        onClick={() => {
                          handleCloseMenu();
                          setSelectedLanguage(language);
                          setShowLanguageForm(true);
                        }}
                        className="text-dark font-weight-light"
                      >
                        <span className="material-icons mr-1">edit_note</span>
                        Edit
                      </MenuItem>
                    </span>

                    <span>
                      <MenuItem
                        disabled={loading}
                        onClick={() => {
                          handleCloseMenu();
                          setOnDelete(true);
                        }}
                        className="text-danger font-weight-light"
                      >
                        <span className="material-icons mr-1">remove</span>{" "}
                        Remove
                      </MenuItem>
                    </span>
                  </Menu>
                </>
              )}
            </>
          }
        />
      </Card>

      <Divider variant="fullWidth" />
      <Divider variant="fullWidth" />

      {onDelete && (
        <AlertDialog
          status={onDelete}
          handleClose={() => setOnDelete(false)}
          handleConfirm={() =>
            deleteUserLanguage(language.id, () => setOnDelete(false))
          }
          title="Delete qualification"
          desc="Are your sure you want to delete this language?"
          disabled={loading}
        />
      )}
    </>
  );
};
